import {
	BulkAction,
	Button,
	designTokens,
	Flex,
	RiCheckLine,
	RiDownload2Line,
	RiFilterOffLine,
	RiFolderZipLine
} from "@itandi/itandi-bb-ui";
import { Dropdown } from "@itandi/itandi-bb-ui/Dropdown";
import { DropdownMenu } from "@itandi/itandi-bb-ui/DropdownMenu";
import type { ReactNode } from "react";
import React, { useState } from "react";
import type { DroppableProvided, DropResult } from "react-beautiful-dnd";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { renderDropDownButton } from "~/components/common/parts/dropdown/DropDownButton";
import { ReportBulkDeleteModal } from "~/components/common/reports/ReportBulkDeleteModal";
import { ReportImagesAreaSpRow } from "~/components/common/reports/ReportImagesAreaSpRow";
import { ReportPdfDownloadModal } from "~/components/common/reports/ReportPdfDownloadModal";
import type { NskResponse } from "~/models/Api";
import type {
	DealReportBulkUpdateParams,
	DealReportImage,
	ReportImageBulkAction
} from "~/models/DealReportImage";
import {
	filterSelectedOwnImages,
	hasOperableSelectedImages,
	imagesExistInReport
} from "~/models/DealReportImage";
import Styles from "./ReportImagesAreaSp.module.scss";

type Props = {
	companyType: "company" | "vendor";
	handleBulkDelete: () => void;
	handleBulkUpdate: (submitValue: DealReportBulkUpdateParams) => Promise<void>;
	handleDestroy: (reportImage: DealReportImage) => Promise<NskResponse>;
	handleDownloadImage: (reportImage: DealReportImage) => void;
	handleDownloadPdf: (imagesPerPage: number) => void;
	handleDownloadZip: () => void;
	handleResetSelectedIds: () => void;
	handleSelectAllImages: () => void;
	handleSelectedDownloadPdf: (imagesPerPage: number) => void;
	handleSelectedDownloadZip: () => void;
	handleUpdate: (submitValue: DealReportImage) => Promise<NskResponse>;
	handleUpdateSeq: (newOrderImages: DealReportImage[]) => Promise<void>;
	onChangeSelectedImage: (reportImageId: number) => void;
	reportImages: DealReportImage[];
	selectedImageIds: readonly number[];
};

export const ReportImagesAreaSp: React.FC<Props> = ({
	reportImages,
	handleDownloadImage,
	handleUpdateSeq,
	handleUpdate,
	handleDestroy,
	handleDownloadPdf,
	handleDownloadZip,
	companyType,
	onChangeSelectedImage,
	selectedImageIds,
	handleSelectedDownloadPdf,
	handleSelectedDownloadZip,
	handleBulkUpdate,
	handleBulkDelete,
	handleResetSelectedIds,
	handleSelectAllImages
}: Props) => {
	// 並び替えのためのstate
	const [orderImages, setOrderImages] = useState<DealReportImage[]>([]);
	const isVendor = companyType === "vendor";
	const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
	const [pdfModalMode, setPdfModalMode] = useState<"all" | "selected" | null>(
		null
	);
	React.useEffect(() => {
		setOrderImages(reportImages);
	}, [reportImages]);
	// 報告書用通し番号
	let tmpSeqNo = 0;
	const onDragEnd = (result: DropResult): void => {
		tmpSeqNo = 0;
		const { destination, source, draggableId } = result;
		if (!destination) {
			return;
		}
		if (
			destination.droppableId === source.droppableId &&
			destination.index === source.index
		) {
			return;
		}
		const moveImage =
			orderImages.find((e: DealReportImage) => String(e.id) === draggableId) ??
			null;
		const newOrderItems: DealReportImage[] = [];
		const wkOrderImages = [...orderImages];
		wkOrderImages.splice(source.index, 1);
		if (moveImage !== null) {
			wkOrderImages.splice(destination.index, 0, moveImage);
		}
		wkOrderImages.forEach((r: DealReportImage, i: number) => {
			newOrderItems.push({ ...r, seq: i + 1 });
		});
		setOrderImages(newOrderItems);
		handleUpdateSeq(newOrderItems);
	};
	const renderRow = (
		reportImage: DealReportImage,
		index: number
	): ReactNode => {
		const displaySeqNo = ((): number | null => {
			if (isVendor) {
				return index + 1;
			}
			if (reportImage.outputPdfImage) {
				tmpSeqNo += 1;
				return tmpSeqNo;
			}
			return null;
		})();
		return (
			<ReportImagesAreaSpRow
				companyType={companyType}
				displaySeqNo={displaySeqNo}
				handleDestroy={handleDestroy}
				handleDownloadImage={handleDownloadImage}
				handleUpdate={handleUpdate}
				handleUpdateSeq={handleUpdateSeq}
				index={index}
				key={reportImage.id}
				onChangeSelectedImage={onChangeSelectedImage}
				reportImage={reportImage}
				selectedImageIds={selectedImageIds}
			/>
		);
	};
	const showBulkAction = React.useMemo(
		(): boolean =>
			hasOperableSelectedImages(reportImages, selectedImageIds, companyType),
		[reportImages, selectedImageIds, companyType]
	);
	const bulkActionList = (): ReportImageBulkAction[] => {
		const actions: ReportImageBulkAction[] = [];
		actions.push({
			children: "画像をダウンロード",
			onClick: handleSelectedDownloadZip
		});
		actions.push({
			children: "報告書をダウンロード",
			onClick: () => {
				setPdfModalMode("selected");
			}
		});
		if (showBulkAction) {
			actions.push({
				children: "施工推奨にする",
				onClick: () => handleBulkUpdate({ construction: "targeted" })
			});
			actions.push({
				children: "施工推奨から外す",
				onClick: () => handleBulkUpdate({ construction: "unspecified" })
			});
		}
		if (isVendor) {
			if (showBulkAction) {
				actions.push({
					children: "画像を共有する",
					onClick: () => handleBulkUpdate({ shared: true })
				});
				actions.push({
					children: "画像を共有しない",
					onClick: () => handleBulkUpdate({ shared: false })
				});
			}
		} else {
			actions.push({
				children: "画像を報告書に含める",
				onClick: () => handleBulkUpdate({ outputPdfImage: true })
			});
			actions.push({
				children: "画像を報告書に含めない",
				onClick: () => handleBulkUpdate({ outputPdfImage: false })
			});
			actions.push({
				children: "備考を報告書に含める",
				onClick: () => handleBulkUpdate({ outputPdfNote: true })
			});
			actions.push({
				children: "備考を報告書に含めない",
				onClick: () => handleBulkUpdate({ outputPdfNote: false })
			});
		}
		if (showBulkAction) {
			// 画像の削除は一番下に表示する
			actions.push({
				children: "画像を削除",
				danger: true,
				onClick: () => {
					setIsBulkModalOpen(true);
				}
			});
		}
		return actions;
	};
	const canDownloadReport = imagesExistInReport(orderImages, isVendor);
	return (
		<div className={Styles.ReportImagesArea}>
			<Flex
				direction="column"
				gap={designTokens["spacing/xs"]}
				mb={designTokens["spacing/xs"]}
			>
				<Button
					disabled={!canDownloadReport}
					endIcon={<RiDownload2Line />}
					onClick={() => {
						setPdfModalMode("all");
					}}
					variant="secondary"
				>
					報告書ダウンロード
				</Button>
				<Button
					disabled={orderImages.length <= 0}
					endIcon={<RiFolderZipLine />}
					onClick={handleDownloadZip}
					variant="secondary"
				>
					画像一括ダウンロード
				</Button>
				<Button
					disabled={reportImages.length === 0}
					onClick={handleSelectAllImages}
					startIcon={<RiCheckLine />}
					variant="secondary"
				>
					全てを選択
				</Button>
				<Button
					disabled={selectedImageIds.length === 0}
					onClick={handleResetSelectedIds}
					startIcon={<RiFilterOffLine />}
					variant="secondary"
				>
					選択をクリア
				</Button>
			</Flex>
			<BulkAction
				actionsArea={
					<Dropdown button={renderDropDownButton("まとめて操作")}>
						{bulkActionList().map((action) => (
							<DropdownMenu
								danger={action.danger}
								key={action.children}
								onClick={action.onClick}
							>
								{action.children}
							</DropdownMenu>
						))}
					</Dropdown>
				}
				selectedCount={selectedImageIds.length}
			>
				<div className={Styles.ReportImageList}>
					<DragDropContext
						onBeforeDragStart={(): void => {
							tmpSeqNo = 0;
						}}
						onDragEnd={onDragEnd}
					>
						<Droppable droppableId="droppable">
							{(
								provided: DroppableProvided
							): React.ReactElement<HTMLElement> => (
								<div ref={provided.innerRef} {...provided.droppableProps}>
									{orderImages.map(renderRow)}
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
				</div>
			</BulkAction>
			{pdfModalMode ? (
				<ReportPdfDownloadModal
					onClose={() => {
						setPdfModalMode(null);
					}}
					onDownload={
						pdfModalMode === "all"
							? handleDownloadPdf
							: handleSelectedDownloadPdf
					}
				/>
			) : null}
			{isBulkModalOpen ? (
				<ReportBulkDeleteModal
					deleteCount={
						filterSelectedOwnImages(reportImages, selectedImageIds, companyType)
							.length
					}
					onClose={() => {
						setIsBulkModalOpen(false);
					}}
					onDelete={handleBulkDelete}
				/>
			) : null}
		</div>
	);
};
